import { ChapterResponse } from '@goldcast/api/content';
import {
  getAbsolutePlayerTime,
  getPreviousSegmentAbsoluteTime,
  getSegmentAtCurrentFrame,
  getSegmentTimeFromPlayerTime
} from './timeUtils';
import { AlignedRecordings } from '@/App/remotion/MainPlayer/stores/alignedRecordings';
import { screenShareBarsColors, sharedVideoBarsColors } from '@/context/TranscriptContext/SpeakerColors';
import { SpeakerWithDetails } from '@/context/TranscriptContext/TranscriptContextTypes';
import { Pronouns } from '@/domains/speaker';
import { CompositeSegment } from '@/App/remotion/MainPlayer/types/Segment/CompositeSegment';
import { Clip } from '@/domains/asset';
import { Segment } from '@/App/remotion/MainPlayer/types/Segment/Segment';
import { SegmentType } from '@/App/remotion/MainPlayer/types/Segment/Type';
import { getClipFPS, isClipAudiogram } from '@/libs/clipUtils';
import { isEntityScreenshare, isEntitySharedVideo } from '@/libs/activityUtils';
import { isScreenShareEnabledForCustomUploadClips, isSplitsInTimelineEnabled } from '@/stores/clip';
import { speakersAnalysisStore } from '@/stores/speakersAnalysis/speakersAnalysis';

export const MINIMUM_CHAPTER_TIME_DIFFERENCE = 3;
export const MINIMUM_SCENE_TIME_DIFFERENCE = 3;

export const getScreenShareSpeakerDetails = (
  recordings: AlignedRecordings[],
  clip?: Clip,
  speakersAnalysisForAllClips?: ReturnType<typeof speakersAnalysisStore.getSnapshot>
) => {
  const stageTimeSpans: Record<number, number> = {};

  const screenShareVideos = recordings.filter(recording => isEntityScreenshare(recording.entity));

  if (screenShareVideos.length > 0) {
    screenShareVideos.forEach(videos => {
      if (videos.stage_time_spans) {
        videos.stage_time_spans.forEach(timeSpan => {
          stageTimeSpans[timeSpan.start_time] = timeSpan.end_time;
        });
      }
    });
  }
  if (clip && isScreenShareEnabledForCustomUploadClips(clip)) {
    const speakersAnalysis = (speakersAnalysisForAllClips ?? speakersAnalysisStore.getSnapshot())[clip.id];

    if (speakersAnalysis?.screenshare_bounds && speakersAnalysis.screenshare_bounds.length > 0) {
      speakersAnalysis.screenshare_bounds.forEach(bound => {
        stageTimeSpans[bound.start_time] = bound.end_time;
      });
    }
  }

  if (Object.keys(stageTimeSpans).length === 0) {
    return null;
  }

  const speakerDetails: SpeakerWithDetails = {
    first_name: 'Shared Screen',
    last_name: '',
    id: 'screen-share',
    key: 'screen-share',
    color: screenShareBarsColors.textColor,
    bgColor: screenShareBarsColors.bgColor,
    lightBgColor: screenShareBarsColors.lightBgColor,
    hoverBgColor: screenShareBarsColors.hoverBgColor,
    speakingSlots: stageTimeSpans,
    parent_id: '',
    company: '',
    title: '',
    profile_picture_url: '',
    pronouns: Pronouns.HE
  };

  return speakerDetails;
};

export const getSharedVideoTrackDetails = (recordings: AlignedRecordings[]) => {
  const sharedVideos = recordings.filter(recording => isEntitySharedVideo(recording.entity));

  if (sharedVideos.length === 0) {
    return null;
  }

  const stageTimeSpans = {};

  sharedVideos.forEach(videos => {
    if (videos.stage_time_spans) {
      videos.stage_time_spans.forEach(timeSpan => {
        stageTimeSpans[timeSpan.start_time] = timeSpan.end_time;
      });
    }
  });
  const speakerDetails: SpeakerWithDetails = {
    first_name: 'Shared Video',
    last_name: '',
    id: 'shared-video',
    key: 'shared-video',
    color: sharedVideoBarsColors.textColor,
    bgColor: sharedVideoBarsColors.bgColor,
    lightBgColor: sharedVideoBarsColors.lightBgColor,
    hoverBgColor: sharedVideoBarsColors.hoverBgColor,
    speakingSlots: stageTimeSpans,
    parent_id: '',
    company: '',
    title: '',
    profile_picture_url: '',
    pronouns: Pronouns.HE
  };

  return speakerDetails;
};

export const canCreateANewChapter = ({
  currentTime,
  chapters,
  clipData
}: {
  currentTime: number;
  chapters: ChapterResponse[];
  clipData: Clip;
}) => {
  const fps = getClipFPS(clipData);
  const isSplitsEnabled = isSplitsInTimelineEnabled(clipData);
  const currentSegment = getSegmentAtCurrentFrame(currentTime, clipData);
  if (!currentSegment || (currentSegment.type !== SegmentType.COMPOSITE && currentSegment.type !== SegmentType.VIDEO)) {
    return false;
  }

  if (
    !isSplitsEnabled &&
    (currentTime < currentSegment.fromFrame / fps || currentTime > currentSegment.toFrame / fps)
  ) {
    return false;
  }

  const newChapterStartTime = isSplitsEnabled
    ? currentTime
    : getSegmentTimeFromPlayerTime(currentTime, clipData, currentSegment.id);

  const hasChapterAtSamePositionOrNextToIt = chapters?.some(
    chapter =>
      chapter.start_time < newChapterStartTime + MINIMUM_CHAPTER_TIME_DIFFERENCE &&
      chapter.start_time > newChapterStartTime - MINIMUM_CHAPTER_TIME_DIFFERENCE
  );
  return !hasChapterAtSamePositionOrNextToIt;
};

export const canAddScene = ({
  compositeSegment,
  currentTime,
  clipData
}: {
  compositeSegment: CompositeSegment;
  currentTime: number;
  clipData: Clip;
}) => {
  const fps = getClipFPS(clipData);
  const trimOffset = compositeSegment.currentBounds.fromFrame;

  if (currentTime < compositeSegment.fromFrame / fps) {
    return false;
  }

  const currentSegmentFrame =
    getAbsolutePlayerTime(currentTime, clipData) * fps -
    getPreviousSegmentAbsoluteTime(compositeSegment, clipData) * fps +
    trimOffset;

  // Check if there's already a scene at this exact position
  const scenes = compositeSegment.scenes || [];
  if (isClipAudiogram(clipData)) {
    return false;
  }
  const hasSceneAtCurrentPosition = scenes.some(
    scene =>
      scene.fromFrame >= currentSegmentFrame - MINIMUM_SCENE_TIME_DIFFERENCE * fps &&
      scene.fromFrame <= currentSegmentFrame + MINIMUM_SCENE_TIME_DIFFERENCE * fps
  );

  if (
    scenes.length === 0 &&
    currentSegmentFrame - MINIMUM_SCENE_TIME_DIFFERENCE * fps <= compositeSegment.currentBounds.fromFrame
  ) {
    // no scenes then don't allow creation of scene at start which is less than MINIMUM_SCENE_TIME_DIFFERENCE * fps
    return false;
  }

  return (
    !hasSceneAtCurrentPosition &&
    currentSegmentFrame + MINIMUM_SCENE_TIME_DIFFERENCE * fps <= compositeSegment.currentBounds.toFrame
  );
};

export const getSegmentWidth = (segment: Segment) => {
  if (segment.type !== SegmentType.COMPOSITE) {
    return segment.toFrame - segment.fromFrame;
  }
  return segment.currentBounds.toFrame - segment.currentBounds.fromFrame;
};

export const prependIntroOutroDisplayText = (segment: Segment, text: string) => {
  if (segment.id === 'intro') {
    return `Intro - ${text}`;
  }
  if (segment.id === 'outro') {
    return `Outro - ${text}`;
  }
  return text;
};

const MAX_SEGMENT_DISPLAY_TEXT_LENGTH = 20;
const ELLIPSIS = '...';

const trimSegmentDisplayText = (text: string) => {
  if (text.length <= MAX_SEGMENT_DISPLAY_TEXT_LENGTH) {
    return text;
  }
  return text.slice(0, MAX_SEGMENT_DISPLAY_TEXT_LENGTH - ELLIPSIS.length) + ELLIPSIS;
};

const getVideoSrcDisplayText = (srcURL: string) => {
  const fileName = srcURL.split('/').pop()?.split('?')[0] ?? '';
  const fileNameWithoutExtension = fileName.split('.').slice(0, -1).join('.');
  const nameWithSpaces = fileNameWithoutExtension.replaceAll(/[_\-]/g, ' ').replaceAll(/ {2,}/g, ' ');
  const cleanedNameWithSpaces = nameWithSpaces.trim();
  if (!cleanedNameWithSpaces) {
    return null;
  }
  const normalized = cleanedNameWithSpaces.charAt(0).toUpperCase() + cleanedNameWithSpaces.slice(1).toLowerCase();
  return normalized;
};

const getSegmentBaseDisplayText = (segment: Segment) => {
  if (segment.type !== SegmentType.VIDEO) {
    return segment.name;
  }

  const srcURL = segment.video.details.src;
  if (!srcURL) {
    return segment.name;
  }

  return getVideoSrcDisplayText(srcURL) ?? segment.name;
};

export const truncateDisplayText = (text: string) => {
  return trimSegmentDisplayText(text);
};

export const getSegmentDisplayText = (segment: Segment) => {
  return getSegmentBaseDisplayText(segment);
};

export const getSegmentDisplayTextWithIntroOutroPrefix = (segment: Segment) => {
  return prependIntroOutroDisplayText(segment, getSegmentDisplayText(segment));
};
